import YAML from 'yaml';
import { CHECK_TYPES } from './grader/checks.js';
import { IRIS_AGENT_FROM_LEVEL } from './iris.js';
import { irisScriptProblems, publicIrisScript, scriptTraps } from './iris-script.js';
/**
 * The warm-up: the optional tutorial offered before a level.
 *
 * It teaches the tools the level needs and nothing about the level's own
 * task, which is what keeps it from being a sixth hint rung. A card is one
 * tool: what it is, why it exists, optionally a diagram of the mechanism,
 * and optionally one rep at a simulated prompt where the learner types the
 * command and sees the output it would print. The prompt is simulated on
 * purpose — it is authored, so it is always right, it costs no VM, and it
 * can answer before the level's workspace has finished seeding.
 *
 * See docs/missions.md, "Warm-up".
 */
/**
 * The diagrams the warm-up can draw. Each name has a component in
 * web/src/components/warmup/figures; naming one that does not exist is a
 * build failure rather than an empty box on the learner's screen, which is
 * the only reason this list lives in the engine.
 */
export const TUTORIAL_FIGURES = [
    'filesystem',
    'redirect',
    'search',
    'three-trees',
    'commit-chain',
    'branch-pointer',
    'reset-modes',
    'revert',
    'remote',
    'merge',
    'conflict',
    'reflog',
    'rebase',
    'stash',
    'test-run',
    'stack-trace',
    'cherry-pick',
    'bisect',
    'squash',
];
/** More than this and it stops being a warm-up. */
const MAX_TUTORIAL_CARDS = 8;
/**
 * A `{{seed.KEY}}` reference: the syntax a mission uses to name a value that
 * does not exist until the level has been seeded (docs/missions.md, "Seed").
 */
const SEED_REF = /\{\{\s*seed\.([A-Za-z0-9_]+)\s*\}\}/g;
/**
 * Render `{{seed.KEY}}` in the prose a learner reads.
 *
 * The grader has a renderer of its own — `applyTemplate` in grader/index.ts —
 * and the two are deliberately not the same function. A check is rendered
 * *strictly*: a missing value throws, because a check that compared against
 * the literal text "{{seed.LOST_COMMIT}}" would be a check quietly asking the
 * wrong question, and a level that cannot grade should say so. Prose is the
 * other way round. The sentence is already on the screen, the learner is
 * already reading it, and a brief that throws is a level that will not open —
 * a far worse outcome than one placeholder left standing in it. So this one
 * leaves a reference it has no value for exactly as it found it.
 *
 * What keeps that lenience from being a way for the bug to ship again is
 * scripts/build-missions.mjs, which refuses to build a catalogue whose prose
 * names a value the level will not be carrying when the prose is read. The
 * renderer is forgiving so that a learner is never stopped; the build is not,
 * so that there is nothing to forgive.
 *
 * Applies to every language by construction: a translation is the same
 * mission with its strings overwritten (i18n.ts, `applyMissionText`), and the
 * Arabic sidecars carry the same `{{seed.KEY}}` references the English does,
 * so rendering here — after the overlay, on the way to the screen — renders
 * both.
 */
export function renderSeed(value, seed) {
    if (typeof value === 'string') {
        return value.replace(SEED_REF, (ref, key) => seed[key] ?? ref);
    }
    if (Array.isArray(value))
        return value.map((v) => renderSeed(v, seed));
    if (value && typeof value === 'object') {
        const out = {};
        for (const [k, v] of Object.entries(value))
            out[k] = renderSeed(v, seed);
        return out;
    }
    return value;
}
/**
 * The level as the client may see it — with every `{{seed.KEY}}` in its prose
 * replaced by the value this level was seeded with.
 *
 * This is where the rendering happens because this is the boundary: the brief,
 * the constraints, the objective titles and the warm-up cross it on their way
 * to the screen, and nothing downstream of it displays anything else. Doing it
 * at build time instead would bake one machine's values into a file that has
 * to be right on every machine, and doing it in the author's YAML would make
 * the value a thing the author has to know before the seed has run.
 *
 * The values are the ones the mission is carrying (`Mission.seed`), which is
 * how a paid level's arrive: they travel with the level, behind the
 * entitlement check, because for many of them the value *is* the answer
 * (docs/cheating.md). There is deliberately no second parameter for a caller
 * with better values to pass — both of the callers in the product are
 * `missions.map(publicMission)`, and a second parameter there is handed the
 * array index. A caller holding a live session, whose seeder knows what
 * `boot.sh` printed a second ago, composes instead:
 * `renderSeed(publicMission(m), session.seed)` walks the finished view and
 * fills anything the mission could not.
 */
export function publicMission(m) {
    // A locked stub has no brief, no objectives and no hints to be public
    // about. The empty values below are not "the level, empty" — they are the
    // absence of a level, and `locked` is what says so. Everything that could
    // act on them (the router, the map, session creation) checks that flag.
    if (m.locked) {
        return {
            id: m.id,
            level: m.level,
            track: m.track,
            requires: m.requires,
            world: m.world,
            ...(m.assesses ? { assesses: m.assesses } : {}),
            xp: m.xp,
            expects_repo: false,
            title: m.title,
            module: m.module,
            concept: m.concept,
            estimated_minutes: m.estimated_minutes,
            cwd: '',
            brief: '',
            constraints: [],
            objectives: [],
            hint_count: 0,
            mastery_tags: m.mastery_tags,
            locked: true,
        };
    }
    const seed = m.seed ?? {};
    // From disk the budget is on `iris`; from a built catalogue it is the only
    // part of it that shipped. Either way the view carries the number alone.
    const irisBudget = m.iris?.budget ?? m.irisBudget;
    return {
        id: m.id,
        level: m.level,
        track: m.track,
        requires: m.requires,
        world: m.world,
        ...(m.assesses ? { assesses: m.assesses } : {}),
        xp: m.xp,
        expects_repo: m.expects_repo,
        title: m.title,
        module: m.module,
        concept: m.concept,
        estimated_minutes: m.estimated_minutes,
        cwd: m.cwd,
        brief: renderSeed(m.brief, seed),
        constraints: renderSeed(m.constraints, seed),
        objectives: m.objectives
            .filter((o) => !o.hidden)
            .map((o) => ({ id: o.id, title: renderSeed(o.title, seed), hidden: false })),
        hint_count: m.hints.length,
        mastery_tags: m.mastery_tags,
        ...(m.situations ? { situations: m.situations } : {}),
        ...(m.tutorial ? { tutorial: renderSeed(m.tutorial, seed) } : {}),
        ...(typeof irisBudget === 'number' ? { irisBudget } : {}),
        ...(m.irisScript ? { irisScript: publicIrisScript(m.irisScript) } : {}),
        ...(typeof m.irisScriptBudget === 'number' ? { irisScriptBudget: m.irisScriptBudget } : {}),
        ...(m.graded ? { graded: m.graded } : {}),
    };
}
export class MissionError extends Error {
    constructor(id, msg) {
        super(`mission "${id}": ${msg}`);
    }
}
function validateCheck(id, where, spec) {
    if (typeof spec.check !== 'string')
        throw new MissionError(id, `${where} is missing "check"`);
    if (!CHECK_TYPES.includes(spec.check))
        throw new MissionError(id, `${where} uses unknown check "${spec.check}"`);
    if (spec.with !== undefined && (typeof spec.with !== 'object' || spec.with === null)) {
        throw new MissionError(id, `${where} "with" must be a mapping`);
    }
    const w = (spec.with ?? {});
    if ((spec.check === 'any_of' || spec.check === 'all_of') && Array.isArray(w.checks)) {
        w.checks.forEach((c, i) => validateCheck(id, `${where}.checks[${i}]`, c));
    }
}
/** A non-empty string, or a thrown MissionError naming where it was missing. */
function str(id, where, value) {
    if (typeof value !== 'string' || value.trim() === '')
        throw new MissionError(id, `${where} must be a non-empty string`);
    return value;
}
/**
 * The optional `tutorial:` block. Everything the client will draw is checked
 * here, because a warm-up is authored text with no test to catch it: an
 * unknown figure name, an `accept:` pattern that does not compile, or a card
 * with no words on it would all reach a learner intact otherwise.
 */
function validateTutorial(id, raw) {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw))
        throw new MissionError(id, '"tutorial" must be a mapping');
    const t = raw;
    for (const key of ['title', 'lede']) {
        if (t[key] !== undefined && typeof t[key] !== 'string')
            throw new MissionError(id, `tutorial.${key} must be a string`);
    }
    if (!Array.isArray(t.cards) || t.cards.length === 0)
        throw new MissionError(id, 'tutorial needs at least one card');
    if (t.cards.length > MAX_TUTORIAL_CARDS) {
        throw new MissionError(id, `a warm-up is at most ${MAX_TUTORIAL_CARDS} cards; this one has ${t.cards.length}`);
    }
    t.cards.forEach((entry, i) => {
        const where = `tutorial.cards[${i}]`;
        if (typeof entry !== 'object' || entry === null || Array.isArray(entry))
            throw new MissionError(id, `${where} must be a mapping`);
        const card = entry;
        str(id, `${where}.tool`, card.tool);
        str(id, `${where}.title`, card.title);
        if (card.body !== undefined && typeof card.body !== 'string')
            throw new MissionError(id, `${where}.body must be a string`);
        if (card.figure !== undefined) {
            if (!TUTORIAL_FIGURES.includes(card.figure)) {
                throw new MissionError(id, `${where}.figure "${String(card.figure)}" is not a figure the client can draw`);
            }
        }
        if (card.try !== undefined) {
            if (typeof card.try !== 'object' || card.try === null || Array.isArray(card.try)) {
                throw new MissionError(id, `${where}.try must be a mapping`);
            }
            const tryIt = card.try;
            str(id, `${where}.try.run`, tryIt.run);
            for (const key of ['output', 'cwd', 'note', 'nudge']) {
                if (tryIt[key] !== undefined && typeof tryIt[key] !== 'string') {
                    throw new MissionError(id, `${where}.try.${key} must be a string`);
                }
            }
            if (tryIt.accept !== undefined) {
                if (typeof tryIt.accept !== 'string')
                    throw new MissionError(id, `${where}.try.accept must be a string`);
                try {
                    new RegExp(tryIt.accept);
                }
                catch (err) {
                    throw new MissionError(id, `${where}.try.accept is not a valid regular expression: ${err.message}`);
                }
            }
        }
        if (card.keys !== undefined) {
            if (!Array.isArray(card.keys))
                throw new MissionError(id, `${where}.keys must be a list`);
            card.keys.forEach((k, j) => {
                if (typeof k !== 'object' || k === null || Array.isArray(k))
                    throw new MissionError(id, `${where}.keys[${j}] must be a mapping`);
                str(id, `${where}.keys[${j}].key`, k.key);
                str(id, `${where}.keys[${j}].title`, k.title);
            });
        }
    });
}
/**
 * The optional `situations:` block.
 *
 * Checked here for the reason the warm-up is: it is authored data with no
 * test of its own until somebody plays the level badly, and every mistake in
 * it fails silently rather than loudly. A dangling `for:` id points the
 * grader's failure message at an objective that does not exist; a question
 * with no right answer can never be got right; a `free_through` above the
 * ladder's height unlocks rungs that are not there. All three would ship.
 *
 * `objectiveIds` and `hintCount` come from the two blocks validated above
 * this one, which is why the call sits where it does: a situation is checked
 * against the level it belongs to, not against a schema in the abstract.
 */
function validateSituations(id, raw, track, objectiveIds, hintCount) {
    // A checkpoint is an assessment (docs/checkpoints.md). Recognizing the
    // state somebody has got themselves into and saying something useful about
    // it is exactly what an assessment must not do, so the key is refused here
    // rather than left to an author to remember.
    if (track === 'checkpoint') {
        throw new MissionError(id, 'a checkpoint carries no "situations": it is an assessment, not a level with help behind it');
    }
    if (!Array.isArray(raw))
        throw new MissionError(id, '"situations" must be a list');
    const seen = new Set();
    raw.forEach((entry, i) => {
        const where = `situations[${i}]`;
        if (typeof entry !== 'object' || entry === null || Array.isArray(entry))
            throw new MissionError(id, `${where} must be a mapping`);
        const sit = entry;
        const sid = str(id, `${where}.id`, sit.id);
        if (seen.has(sid))
            throw new MissionError(id, `duplicate situation id "${sid}"`);
        seen.add(sid);
        const named = `situation "${sid}"`;
        validateCheck(id, named, sit);
        str(id, `${named} "say"`, sit.say);
        if (sit.for !== undefined) {
            if (!Array.isArray(sit.for))
                throw new MissionError(id, `${named} "for" must be a list of objective ids`);
            for (const ref of sit.for) {
                if (typeof ref !== 'string' || !objectiveIds.has(ref)) {
                    throw new MissionError(id, `${named} explains "${String(ref)}", which is not an objective id on this level`);
                }
            }
        }
        // `ask` and `choices` are one thing in two keys, so either without the
        // other is an author halfway through writing a question — a question with
        // no answers, or answers to a question nobody is asked.
        if (sit.choices !== undefined && sit.ask === undefined)
            throw new MissionError(id, `${named} has choices but no "ask"`);
        if (sit.ask !== undefined) {
            str(id, `${named} "ask"`, sit.ask);
            if (sit.choices === undefined)
                throw new MissionError(id, `${named} asks a question with no choices to answer it`);
        }
        if (sit.choices !== undefined) {
            if (!Array.isArray(sit.choices) || sit.choices.length < 2 || sit.choices.length > 4) {
                throw new MissionError(id, `${named} needs between two and four choices`);
            }
            let right = 0;
            sit.choices.forEach((c, j) => {
                const cw = `${named} choices[${j}]`;
                if (typeof c !== 'object' || c === null || Array.isArray(c))
                    throw new MissionError(id, `${cw} must be a mapping`);
                const choice = c;
                str(id, `${cw}.text`, choice.text);
                // Required on the wrong ones too: naming the misconception is the
                // whole value of the question, and a choice with nothing to say when
                // it is picked is a choice that teaches by scolding.
                str(id, `${cw}.reply`, choice.reply);
                if (choice.right !== undefined && typeof choice.right !== 'boolean') {
                    throw new MissionError(id, `${cw}.right must be true or absent`);
                }
                if (choice.right === true)
                    right += 1;
            });
            if (right !== 1)
                throw new MissionError(id, `${named} must have exactly one choice marked "right: true", not ${right}`);
        }
        if (sit.look !== undefined)
            str(id, `${named} "look"`, sit.look);
        if (sit.free_through !== undefined) {
            const n = sit.free_through;
            if (!Number.isInteger(n) || n < 1 || n > hintCount) {
                throw new MissionError(id, `${named} "free_through" must be an integer between 1 and this level's hint count (${hintCount}), not ${String(n)}`);
            }
        }
    });
}
/** A trap id: lower-case words joined by single hyphens, because it is also a file name. */
const TRAP_ID = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
/**
 * The optional `iris:` block (docs/iris.md, "Planted mistakes").
 *
 * Refused on anything but a checkpoint: a level's Iris is decided by its
 * number (`irisMode`), and planting mistakes is a thing only an assessment
 * may do. Checked strictly, unknown keys included, because every slip here
 * fails in the quiet direction. A `caught_by` naming no objective is a trap
 * that nothing catches, so approving it would pass; a trap with an empty
 * directive is one Iris is never told to spring, so the checkpoint claims a
 * mistake that is not there.
 */
function validateIris(id, raw, track, level, objectiveIds) {
    // A checkpoint, or a general level where Iris is the live agent: planting a
    // mistake on a level where she is scripted or absent would be a directive
    // nobody ever sends.
    const onLevel = track === 'general' && typeof level === 'number' && level >= IRIS_AGENT_FROM_LEVEL;
    if (track !== 'checkpoint' && !onLevel) {
        throw new MissionError(id, `"iris" is for a checkpoint or a general level from ${IRIS_AGENT_FROM_LEVEL}, where Iris is the live agent; this is ${track} level ${String(level)}`);
    }
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw))
        throw new MissionError(id, '"iris" must be a mapping');
    const block = raw;
    for (const key of Object.keys(block)) {
        if (key !== 'budget' && key !== 'traps')
            throw new MissionError(id, `iris has an unknown key "${key}"; it takes "budget" and "traps"`);
    }
    const budget = block.budget;
    if (onLevel) {
        if (budget !== undefined)
            throw new MissionError(id, 'iris.budget is a checkpoint\'s; a level carries traps alone');
    }
    else if (typeof budget !== 'number' || !Number.isInteger(budget) || budget <= 0) {
        throw new MissionError(id, `iris.budget must be a positive integer (tokens for one attempt), not ${JSON.stringify(budget ?? null)}`);
    }
    if (!Array.isArray(block.traps) || block.traps.length === 0) {
        throw new MissionError(id, 'iris needs at least one trap: a checkpoint carries Iris only once approving everything she proposes fails it');
    }
    const seen = new Set();
    block.traps.forEach((entry, i) => {
        const where = `iris.traps[${i}]`;
        if (typeof entry !== 'object' || entry === null || Array.isArray(entry))
            throw new MissionError(id, `${where} must be a mapping`);
        const trap = entry;
        for (const key of Object.keys(trap)) {
            if (key !== 'id' && key !== 'directive' && key !== 'caught_by') {
                throw new MissionError(id, `${where} has an unknown key "${key}"; a trap takes "id", "directive" and "caught_by"`);
            }
        }
        const tid = str(id, `${where}.id`, trap.id);
        if (!TRAP_ID.test(tid))
            throw new MissionError(id, `trap id "${tid}" must be kebab-case, because it names wrong/iris-${tid}.sh`);
        if (seen.has(tid))
            throw new MissionError(id, `duplicate trap id "${tid}"`);
        seen.add(tid);
        str(id, `trap "${tid}" directive`, trap.directive);
        if (!Array.isArray(trap.caught_by) || trap.caught_by.length === 0) {
            throw new MissionError(id, `trap "${tid}" caught_by must list the objective ids that fail when the mistake is approved`);
        }
        const named = new Set();
        for (const ref of trap.caught_by) {
            if (typeof ref !== 'string' || !objectiveIds.has(ref)) {
                throw new MissionError(id, `trap "${tid}" is caught by "${String(ref)}", which is not an objective id on this checkpoint`);
            }
            if (named.has(ref))
                throw new MissionError(id, `trap "${tid}" names "${ref}" twice in caught_by`);
            named.add(ref);
        }
    });
}
export function validate(raw, dirName) {
    const id = typeof raw.id === 'string' ? raw.id : dirName;
    if (raw.id !== dirName)
        throw new MissionError(id, `id must equal its directory name "${dirName}"`);
    for (const key of ['title', 'module', 'concept', 'brief', 'reflection', 'world']) {
        if (typeof raw[key] !== 'string' || raw[key].trim() === '')
            throw new MissionError(id, `"${key}" is required`);
    }
    if (!Number.isInteger(raw.level) || raw.level < 0)
        throw new MissionError(id, '"level" must be an integer >= 0');
    // Required, not defaulted, for the same reason "requires" is.
    //
    // It used to fall back to `100 + level * 20`, which read as sensible while
    // there was one track and `level` was a position in the whole course. It is
    // neither now: `level` is a position **within a track** (§5), so the first
    // level of a track would silently price at 100 while the level before it -
    // the end of the general track, which it directly follows - is at 2080. A
    // mission that forgets its xp is not a mission worth guessing for, and the
    // guess is invisible in the one place it is wrong.
    if (!Number.isInteger(raw.xp) || raw.xp < 0) {
        throw new MissionError(id, '"xp" is required: an integer >= 0, because a position within a track cannot imply one');
    }
    if (raw.track !== undefined && (typeof raw.track !== 'string' || raw.track.trim() === '')) {
        throw new MissionError(id, '"track" must be a non-empty string');
    }
    // Required, not defaulted. A mission that forgets it is a mission with no
    // gate, and the safe reading of a missing prerequisite list is "refuse",
    // which is unhelpful; better to refuse the build instead.
    if (!Array.isArray(raw.requires)) {
        throw new MissionError(id, '"requires" is required: a list of mission ids, empty for the level that starts a track');
    }
    for (const r of raw.requires) {
        if (typeof r !== 'string' || r.trim() === '')
            throw new MissionError(id, '"requires" must hold mission ids');
        if (r === dirName)
            throw new MissionError(id, 'a mission cannot require itself');
    }
    if (!Array.isArray(raw.objectives) || raw.objectives.length === 0)
        throw new MissionError(id, 'at least one objective is required');
    const ids = new Set();
    raw.objectives.forEach((o, i) => {
        if (typeof o.id !== 'string')
            throw new MissionError(id, `objectives[${i}] needs an id`);
        if (ids.has(o.id))
            throw new MissionError(id, `duplicate objective id "${o.id}"`);
        ids.add(o.id);
        if (typeof o.title !== 'string')
            throw new MissionError(id, `objective "${o.id}" needs a title`);
        validateCheck(id, `objective "${o.id}"`, o);
        if (o.requires !== undefined) {
            if (!Array.isArray(o.requires))
                throw new MissionError(id, `objective "${o.id}" requires must be a list of objective ids`);
            for (const r of o.requires) {
                if (typeof r !== 'string' || !ids.has(r)) {
                    throw new MissionError(id, `objective "${o.id}" requires "${String(r)}", which is not an earlier objective id`);
                }
            }
        }
        if (o.diagnostics !== undefined) {
            if (!Array.isArray(o.diagnostics))
                throw new MissionError(id, `objective "${o.id}" diagnostics must be a list`);
            o.diagnostics.forEach((d, j) => {
                validateCheck(id, `objective "${o.id}" diagnostics[${j}]`, d);
                if (typeof d.message !== 'string')
                    throw new MissionError(id, `objective "${o.id}" diagnostics[${j}] needs a message`);
            });
        }
    });
    const hints = (raw.hints ?? []);
    if (!Array.isArray(hints))
        throw new MissionError(id, 'hints must be a list');
    hints.forEach((h, i) => {
        if (h.level !== i + 1)
            throw new MissionError(id, `hints must be numbered 1..n in order (hint ${i} has level ${String(h.level)})`);
        if (typeof h.title !== 'string' || typeof h.text !== 'string')
            throw new MissionError(id, `hint ${i + 1} needs title and text`);
    });
    if (hints.length > 5)
        throw new MissionError(id, 'the hint ladder has five levels (question, direction, tool, explanation, solution)');
    if (raw.situations !== undefined) {
        validateSituations(id, raw.situations, typeof raw.track === 'string' ? raw.track : 'general', ids, hints.length);
    }
    // The boot step is a file, like the seed it follows, and a key here would
    // be silently ignored: nothing in this parser rejects a field it does not
    // know, so an author who wrote their boot script into the YAML would get a
    // level that seeds, opens, and has none of its processes running — the
    // exact failure the step exists to prevent, arriving quietly.
    if (raw.boot !== undefined) {
        throw new MissionError(id, 'the boot step is a "boot.sh" file beside seed.sh, not a "boot:" key in mission.yaml');
    }
    if (raw.tutorial !== undefined)
        validateTutorial(id, raw.tutorial);
    if (raw.iris !== undefined)
        validateIris(id, raw.iris, typeof raw.track === 'string' ? raw.track : 'general', raw.level, ids);
    if (raw.iris_script !== undefined) {
        // A general level of any number, or a checkpoint: either is scripted
        // whatever its number (`irisMode`, docs/designed-agents.md). On a
        // checkpoint the agents are the assessment's, and the learner's own step
        // is proved by iris-finish.sh beside it (check-missions). On the AI and
        // security tracks a script would be lines nobody ever reads.
        const track = typeof raw.track === 'string' ? raw.track : 'general';
        if (track !== 'general' && track !== 'checkpoint') {
            throw new MissionError(id, `"iris_script" is for general levels and checkpoints, where agents may be designed; this is ${track}`);
        }
        // A level has one kind of agent. Live traps beside a script would be
        // directives for an agent the level never shows.
        if (raw.iris !== undefined)
            throw new MissionError(id, 'a level has one kind of agent: "iris_script" or an "iris:" block, not both');
        const problems = irisScriptProblems(raw.iris_script, ids);
        if (problems.length > 0)
            throw new MissionError(id, problems[0]);
    }
    if (raw.iris_script_budget !== undefined) {
        // Display and data only for now: it turns the panel's running count
        // into used / budget, and grades nothing.
        if (raw.iris_script === undefined)
            throw new MissionError(id, '"iris_script_budget" is the token budget of an "iris_script", and this level has none');
        const b = raw.iris_script_budget;
        if (typeof b !== 'number' || !Number.isInteger(b) || b <= 0) {
            throw new MissionError(id, `"iris_script_budget" must be a positive integer (tokens), not ${JSON.stringify(b)}`);
        }
    }
    // Written by the build in place of `iris`, never by an author. A hand-typed
    // one would give the tab a budget with no traps behind it.
    if (raw.irisBudget !== undefined) {
        throw new MissionError(id, '"irisBudget" is written by the build; an author gives a checkpoint an "iris:" block');
    }
    if (raw.plausibility !== undefined) {
        const p = raw.plausibility;
        if (typeof p !== 'object' || p === null || Array.isArray(p))
            throw new MissionError(id, '"plausibility" must be a mapping');
        for (const key of ['min_seconds', 'min_commands']) {
            const v = p[key];
            if (v !== undefined && (typeof v !== 'number' || !Number.isFinite(v) || v < 0)) {
                throw new MissionError(id, `plausibility.${key} must be a number >= 0`);
            }
        }
    }
}
/** The optional `plausibility:` block, in the engine's camel case. Absent keys stay absent so the defaults apply. */
function parsePlausibility(raw) {
    if (typeof raw !== 'object' || raw === null)
        return undefined;
    const p = raw;
    const out = {};
    if (typeof p.min_seconds === 'number')
        out.minSeconds = p.min_seconds;
    if (typeof p.min_commands === 'number')
        out.minCommands = p.min_commands;
    return out;
}
/**
 * The optional `tutorial:` block, normalised so the client renders a complete
 * shape rather than filling gaps itself. Validation has already run, so every
 * field here is the type it claims to be; what is left is defaulting, and the
 * one rule worth stating twice: a `try` with no `output` is a command that
 * prints nothing, not a card that forgot its output.
 */
function parseTutorial(raw) {
    if (typeof raw !== 'object' || raw === null)
        return undefined;
    const t = raw;
    const cards = t.cards.map((c) => {
        const card = { tool: c.tool, title: c.title };
        if (typeof c.label === 'string')
            card.label = c.label;
        if (typeof c.body === 'string')
            card.body = c.body;
        if (typeof c.figure === 'string')
            card.figure = c.figure;
        if (c.try) {
            const raw = c.try;
            const tryIt = { run: raw.run, output: typeof raw.output === 'string' ? raw.output : '' };
            if (typeof raw.accept === 'string')
                tryIt.accept = raw.accept;
            if (typeof raw.cwd === 'string')
                tryIt.cwd = raw.cwd;
            if (typeof raw.note === 'string')
                tryIt.note = raw.note;
            if (typeof raw.nudge === 'string')
                tryIt.nudge = raw.nudge;
            card.try = tryIt;
        }
        if (Array.isArray(c.keys)) {
            card.keys = c.keys.map((k) => ({ key: k.key, title: k.title }));
        }
        return card;
    });
    const tutorial = { cards };
    if (typeof t.title === 'string')
        tutorial.title = t.title;
    if (typeof t.lede === 'string')
        tutorial.lede = t.lede;
    return tutorial;
}
/**
 * Parse one mission.yaml. `dirName` is the directory the mission was authored
 * in, which its id must equal.
 *
 * `scripts` is empty in a browser build. When a seed body is given the shared
 * lib is prepended here, so the "the lib runs before every seed" rule has one
 * home whatever assembled the strings.
 */
export function parseMission(yamlText, dirName, scripts = {}) {
    const raw = YAML.parse(yamlText);
    validate(raw, dirName);
    const mission = {
        id: dirName,
        level: raw.level,
        track: typeof raw.track === 'string' ? raw.track : 'general',
        requires: raw.requires,
        world: raw.world,
        ...(typeof raw.assesses === 'string' && raw.assesses.trim() !== '' ? { assesses: raw.assesses } : {}),
        xp: raw.xp,
        expects_repo: raw.expects_repo !== false,
        title: raw.title,
        module: raw.module,
        concept: raw.concept,
        estimated_minutes: typeof raw.estimated_minutes === 'number' ? raw.estimated_minutes : 10,
        cwd: typeof raw.cwd === 'string' ? raw.cwd : '',
        brief: raw.brief,
        constraints: Array.isArray(raw.constraints) ? raw.constraints : [],
        objectives: raw.objectives,
        hints: (raw.hints ?? []),
        reflection: raw.reflection,
        mastery_tags: Array.isArray(raw.mastery_tags) ? raw.mastery_tags : [],
    };
    // Kept as authored. Unlike the warm-up there is nothing to normalise: the
    // optional keys are optional all the way down, and a `choices` absent here
    // means the same thing everywhere it is read.
    if (Array.isArray(raw.situations))
        mission.situations = raw.situations;
    const tutorial = parseTutorial(raw.tutorial);
    if (tutorial)
        mission.tutorial = tutorial;
    const plausibility = parsePlausibility(raw.plausibility);
    if (plausibility)
        mission.plausibility = plausibility;
    // Validated above, so only the shape is left to make. `caught_by` is sorted
    // because it is compared as a set with a wrong script's expect-fail header,
    // and a directive is trimmed because `>` leaves a newline on the end of it.
    if (raw.iris !== undefined) {
        const block = raw.iris;
        mission.iris = {
            ...(block.budget !== undefined ? { budget: block.budget } : {}),
            traps: block.traps.map((t) => ({ id: t.id, directive: t.directive.trim(), caught_by: [...t.caught_by].sort() })),
        };
    }
    if (Array.isArray(raw.iris_script)) {
        mission.irisScript = raw.iris_script.map((b) => ({
            ...b,
            say: b.say.trim(),
            // Only the newline a `|` block leaves on the end: a diff's leading
            // spaces are part of what it says.
            ...(b.show !== undefined ? { show: b.show.replace(/\s+$/, '') } : {}),
            ...(b.caught_by ? { caught_by: [...b.caught_by].sort() } : {}),
        }));
    }
    if (typeof raw.iris_script_budget === 'number')
        mission.irisScriptBudget = raw.iris_script_budget;
    if (scripts.seed !== undefined)
        mission.seedScript = `${scripts.lib ?? ''}\n${scripts.seed}`;
    // An empty boot.sh is a level with no boot step rather than one with an
    // empty one, so the absence of this field means the same thing whichever
    // way the file was left. The boot lib is prepended here for the reason the
    // seed lib is: the rule has one home, whatever assembled the strings.
    if (scripts.boot !== undefined && scripts.boot !== null && scripts.boot.trim() !== '') {
        mission.boot = `${scripts.bootLib ?? ''}\n${scripts.boot}`;
    }
    if (scripts.solution !== undefined)
        mission.solutionScript = scripts.solution;
    if (scripts.irisFinish)
        mission.irisFinishScript = scripts.irisFinish;
    if (scripts.wrong !== undefined)
        mission.wrongScripts = scripts.wrong;
    if (scripts.right !== undefined && scripts.right.length > 0)
        mission.rightScripts = scripts.right;
    if (scripts.article !== undefined)
        mission.article = scripts.article;
    return mission;
}
/** A wrong script's `# expect-fail:` header as sorted objective ids, or null when it has none. */
export function expectFailOf(script) {
    const m = /^#\s*expect-fail:\s*(.+)$/m.exec(script);
    if (!m)
        return null;
    return m[1].split(',').map((s) => s.trim()).filter(Boolean).sort();
}
/**
 * Everything missing from the proof that a checkpoint's traps are caught, as
 * sentences, and an empty list when there is nothing to prove.
 *
 * The rule (docs/iris.md): every trap has a `wrong/iris-<id>.sh`, the
 * reference solution with that mistake applied, whose `# expect-fail:` set is
 * the trap's `caught_by` exactly. That script is then run like every other
 * wrong script (scripts/check-missions.mjs, server/test/missions.test.ts),
 * which proves the header against the grader. The two halves together are
 * what make "approving the mistake fails these objectives" a fact rather than
 * an author's belief. An `iris-` script that names no trap is refused too: it
 * is a proof of a mistake nobody plants, or a trap renamed without its proof.
 *
 * Static, so it needs the wrong scripts on the mission: an author tool's
 * mission, loaded from disk. A browser build has none and must not ask.
 */
export function irisProofProblems(m) {
    const problems = [];
    const scripts = new Map((m.wrongScripts ?? []).map((w) => [w.name, w.script]));
    // A checkpoint's traps and a scripted level's planted beats are proved alike.
    const traps = [...(m.iris?.traps ?? []), ...scriptTraps(m.irisScript)];
    for (const trap of traps) {
        const name = `iris-${trap.id}`;
        const script = scripts.get(name);
        if (script === undefined) {
            problems.push(`trap "${trap.id}" has no wrong/${name}.sh proving that approving it fails ${trap.caught_by.join(', ')}`);
            continue;
        }
        const expected = expectFailOf(script);
        if (expected === null) {
            problems.push(`wrong/${name}.sh has no "# expect-fail:" header; it must be the trap's caught_by: ${trap.caught_by.join(', ')}`);
            continue;
        }
        const caught = [...trap.caught_by].sort();
        if (expected.join(',') !== caught.join(',')) {
            problems.push(`wrong/${name}.sh expects to fail ${expected.join(', ') || 'nothing'}, but trap "${trap.id}" is caught_by ${caught.join(', ')}; the two must be the same set`);
        }
    }
    const trapIds = new Set(traps.map((t) => t.id));
    for (const name of scripts.keys()) {
        if (name.startsWith('iris-') && !trapIds.has(name.slice('iris-'.length))) {
            problems.push(`wrong/${name}.sh proves a trap this mission does not plant`);
        }
    }
    return problems;
}
//# sourceMappingURL=missions.js.map